import {t} from "../../i18n";

import "./Results.css";
import MatchVideo, {VideoColors, videos} from "../components/MatchVideo";
import {useTimer} from "../../hooks";
import {ReactNode, useContext, useEffect, useState} from "react";
import {DisplayConfig} from "../Config";
import {
    AllianceColor,
    DisplayCommand,
    HighScoreLevel,
    MatchAlliance,
    MatchResultsTeam,
    RankMove,
    SeriesAllianceState,
    ShowResults,
    TournamentLevel
} from "../../javaTypes";
import Blank from "./Blank";
import ReactConfetti from "react-confetti";
import DisplayParams from "../DisplayParams";
import {RankingData} from "../rankingDataState";
import {bracketTargets, isDoubleElimsSeriesDescription, isSeriesAllianceDescription} from "../util";
import PrimaryMatch from "../layouts/PrimaryMatch";
import {QRCodeSVG} from "qrcode.react";
import {ScoreType} from "../components/decode/FullScore";
import FormattedSeed from "../components/FormattedSeed";
import EventLogo from "../components/EventLogo";
import {ReactSVG} from "react-svg";
import {useAsset} from "../../AssetHelper";
import ScrollingText from "../components/ScrollingText";
import ReactPlayer from "react-player/file";

function RefCard({card} : {
    card : number
}) {
    if(card === 0) return null;

    return <div className={`resultsCards ${card & 1 && card & 2 ? "twoCards" : ""}`}>
        &nbsp;
        {card & 1 ? <div className={"yellowCard"} /> : null }
        {card & 2 ? <div className={"redCard"} /> : null }
    </div>
}

RefCard.propTypes = {};

function ResultsSet({color, alliance, scoreDetail, otherScoreDetail, tournamentLevel, target, record, seed, score, winner, highScore, highScoreFlavor, rightAligned, hideRank, showLeagueRankings} : {
    color: 'red' | 'blue' | 'orange',
    alliance: Pick<MatchAlliance<MatchResultsTeam>, "teams">,
    scoreDetail: Partial<ScoreType>,
    otherScoreDetail: Partial<ScoreType>,
    tournamentLevel: TournamentLevel,
    target?: ReactNode,
    record?: number,
    seed: string,
    score: number,
    winner?: AllianceColor,
    highScore: boolean,
    highScoreFlavor: string,
    rightAligned: boolean,
    hideRank?: boolean,
    showLeagueRankings?: boolean,
}) {
    let teams = !alliance.teams ? [] : alliance.teams;
    const hasRank = tournamentLevel == TournamentLevel.QUALIFICATION;
    const isPlayoffs = tournamentLevel == TournamentLevel.PLAYOFF;
    const items = teams.map((team, idx) => {
        if (team.number === "") {
            return null;
        }
        const rank = !hasRank || hideRank ? '' : (team.ranking < 0 ? t`NP` : (team.ranking === 0 ? '' : team.ranking));
        const leagueRank = !hasRank || hideRank ? '' : (team.leagueRanking < 0 ? t`NP` : team.leagueRanking);
        const dir = hideRank ? RankMove.NONE : team.rankMove;
        return <div key={idx} className={"resultsTeam"}>
            <span className={"teamNumberColumn"}><div className={`team-avatar team-${team.number} mr-3`}></div> {team.number}</span>
            <RefCard card={isPlayoffs ? 0 : team.card}/>
            { hasRank ? <>
                {showLeagueRankings ? <span className={"leagueRankColumn"}>{leagueRank ? `LR ${leagueRank}` : null}</span> : null}
                <span className={"rankColumn"}>{rank}</span>
                <span className={"dirColumn"}>{(dir === RankMove.DOWN ? <i className={"fa fa-arrow-down"}></i> : (dir === RankMove.UP ?
                  <i className={"fa fa-arrow-up"}></i> : '-'))}</span>
            </> : null}
        </div>;
    })

    const showWinner = color !== 'orange' && (!winner || winner.toLowerCase() === color);

    const earnedFirstWinRp = (!winner || winner?.toLowerCase() == color)
    const earnedSecondAndThirdWinRp = winner?.toLowerCase() == color
    const earnedFirstGameRp = scoreDetail.movementRP
    const earnedSecondGameRp = scoreDetail.goalRP
    const goalRPIcon = useAsset("img/decode/artifact-white.svg")
    const earnedThirdGameRp = scoreDetail.patternRP

    // Only applicable for round robin
    const hasCp = isPlayoffs && teams[0].ranking != 0;
    const earnedCP = (teams[0].card & 2) ? 0 : (!winner ? 1 : (winner?.toLowerCase() == color ? 2 : 0));

    return <div className={`resultsSet ${color} ${rightAligned ? 'right' : ''} ${showLeagueRankings ? 'hasLeagueRankings' : 'null'} ${showWinner ? '' : 'noWinner'}`}>
        <svg width="960px" height="240px" className={`scoreBackground ${rightAligned ? 'right' : ''}`}>
            <polygon points={rightAligned ? "0,0 960,0 960,40 360,40 360,240 0,240" : "0,0 960,0 960,240 600,240 600,40 0,40"} />
        </svg>
        {color == "orange" ? <svg width="960px" height="135px" className={"scoreBackground right"}>
            <polygon points={"0,0 960,0 960,40 323,40 210,135 0,135"} />
        </svg> : null}
        <div className={"scoreRegion"}>
            {isPlayoffs && seed ? <div className={"allianceName"}><ScrollingText><FormattedSeed seed={seed} /></ScrollingText></div> :
              <span className={"allianceName"}>{color == 'red' ? 'Red' : 'Blue'}</span>}
            <span className={"scoreSpan"}>{score}</span>
            {showWinner ? <><span className={`winSpan ${winner ? 'won' : ''}`}>{!winner ? t`Tie` : t`Winner`}</span>{winner ? <i className={"fa fa-trophy"} /> : null}</> : null}
            {highScore ? <span className={"highScoreBanner"}><i className={"fa fa-star"}></i> {highScoreFlavor} <i className={"fa fa-star"}></i></span> : null}
        </div>
        <div className={`resultsUpperWrapper`}>
            {isPlayoffs && teams[0].card ? <div className={"elimsCard"}>{t`Issued Card:`} <RefCard card={teams[0].card} /></div> : null}
            {isPlayoffs && teams[0].ranking != 0 ? <div className={"elimsRanking"}>
                <span className={"rankLabelColumn"}>{t`Rank`}</span>
                <span className={"rankColumn"}>{teams[0].ranking}</span>
                <span className={"dirColumn"}>{(teams[0].rankMove === RankMove.DOWN ? <i className={"fa fa-arrow-down"}></i> : (teams[0].rankMove === RankMove.UP ?
                    <i className={"fa fa-arrow-up"}></i> : '-'))}</span>
            </div> : null}
            {items}
            {isPlayoffs && target ? <div className={"elimsMovement"}>{target}</div> : null}
        </div>
        <div className={"resultsComponents"}>
            <span>{scoreDetail.autoLeavePoints || 0}</span>
            <span>{(scoreDetail.autoArtifactPoints || 0) + (scoreDetail.teleopArtifactPoints || 0) + (scoreDetail.teleopDepotPoints || 0)}</span>
            <span>{(scoreDetail.autoPatternPoints || 0) + (scoreDetail.teleopPatternPoints || 0)}</span>
            <span>{scoreDetail.teleopBasePoints || 0}</span>
            <span>{otherScoreDetail.foulPointsCommitted || 0}</span>
            {isPlayoffs && !target && record != undefined ? <span className={"win-banner"}>{record}</span> : null}
        </div>
        {hasRank ? <div className={"earnedRpContainer"}>
            <span>{t`Ranking Points`}</span>
            <div className={"earnedRp"}>
                <span className={`rpBadge ${earnedFirstWinRp ? 'earned' : ''}`}><i className={"fa fa-trophy"}></i></span>
                <span className={`rpBadge ${earnedSecondAndThirdWinRp ? 'earned' : ''}`}><i className={"fa fa-trophy"}></i></span>
                <span className={`rpBadge ${earnedSecondAndThirdWinRp ? 'earned' : ''}`}><i className={"fa fa-trophy"}></i></span>
                <span className={`rpBadge ${earnedFirstGameRp ? 'earned' : ''}`}><i className={"fa fa-robot"}></i></span>
                <span className={`rpBadge ${earnedSecondGameRp ? 'earned' : ''}`}>
                    <ReactSVG src={goalRPIcon} />
                </span>
                <span className={`rpBadge ${earnedThirdGameRp ? 'earned' : ''}`}>
                    <div className={"motif"}>
                        <ReactSVG src={goalRPIcon} />
                        <ReactSVG src={goalRPIcon} />
                        <ReactSVG src={goalRPIcon} />
                    </div>
                </span>
            </div>
        </div> : null}
        {hasCp ? <div className={"earnedCpContainer"}>
            <span>{t`Championship Points`}</span>
            <div className={"earnedRp"}>
                <span className={`rpBadge ${earnedCP >= 1 ? 'earned' : ''}`}><i className={"fa fa-trophy"}></i></span>
                <span className={`rpBadge ${earnedCP >= 2 ? 'earned' : ''}`}><i className={"fa fa-trophy"}></i></span>
            </div>
        </div> : null}
    </div>;
}

function colorToVideo(key?: AllianceColor | null): VideoColors {
    if(key == AllianceColor.RED) {
        return 'red'
    } else if (key == AllianceColor.BLUE) {
        return 'blue'
    } else {
        return 'tie'
    }
}

const HIGH_SCORE_TEXT: Record<HighScoreLevel, string> = {
    [HighScoreLevel.DIVISION]: t`Division High Score`,
    [HighScoreLevel.EVENT]: t`Event High Score`,
    [HighScoreLevel.REGION]: t`Region High Score`,
    [HighScoreLevel.WORLD]: t`World High Score`,
}

export default function Results({config, command, nowCallback, showLeagueRankings, missingLeague} : {
    config: DisplayConfig,
    command: DisplayCommand<ShowResults>,
    nowCallback: () => number,
    showLeagueRankings?: boolean,
    missingLeague?: boolean
}) {
    const [lateRender, setLateRender] = useState(false);
    const [resultsHidden, setResultsHidden] = useState(false);
    const params = useContext(DisplayParams);
    useEffect(() => {
        {/* Don't play the video if we refresh the page */}
        setLateRender(nowCallback() - command.ts > 1000);
        setResultsHidden(false);
        setTimeout(() => setResultsHidden(true), 12500)
    }, [command.ts]);
    const videoDisabled = command.params.singleTeam || lateRender || command.params.skipAnimation;
    const { remaining, running } = useTimer({
        startAt: command.ts,
        duration: videoDisabled ? 0 : 6,
        nowCallback: nowCallback
    });
    const state = useContext(RankingData);

    const shouldHaveConfetti = command.params.isEventConclusion;
    const { running: confettiRunning } = useTimer({
        startAt: command.ts,
        duration: shouldHaveConfetti ? 65 : 0,
        nowCallback: nowCallback
    })

    // TODO update for CS
    if(config.type == 'cmpBar') {
        let winnerText = t`Tie!`;
        if(command.params.winner == AllianceColor.BLUE) {
            winnerText = t`Blue Wins!`;
        }
        if(command.params.winner == AllianceColor.RED) {
            winnerText = t`Red Wins!`;
        }

        if(resultsHidden || videoDisabled) {
            return <Blank config={config} />;
        } else {
            return <div>
                <div className={`winner ${command.params.winner?.toLowerCase()}`}>{winnerText}</div>
                <ReactPlayer playing url={videos.SandRising} width={"896px"} height={"128px"}
                             style={{position: "fixed", left: 0, top: 0, zIndex: 10, opacity: 1}}
                             className={"matchResultBarVideo"}
                />
            </div>;
        }
    }

    const structure = state[params.divisionNumber || 0]?.playoffStructure;
    const seriesDescription = structure?.series.find(s => s.series == command.params.series);
    const [winnerTarget, loserTarget] = command.params.isEventConclusion || command.params.isDivisionConclusion ? [null, null] : bracketTargets(structure, command.params.series);
    const winnerTargetColor = winnerTarget ? ((isSeriesAllianceDescription(winnerTarget.red) && winnerTarget.red.series == command.params.series && winnerTarget.red.state == SeriesAllianceState.WINNER) ? t`Red` : t`Blue`) : null
    const loserTargetColor = loserTarget ? ((isSeriesAllianceDescription(loserTarget.red) && loserTarget.red.series == command.params.series && loserTarget.red.state == SeriesAllianceState.LOSER) ? t`Red` : t`Blue`) : null
    const isQD = false; // TODO for premier
    const winnerTargetString = winnerTarget ?
        <>{t`Advances to:`} <br/> {winnerTarget.name} <br/> Will be {winnerTargetColor} Alliance</> :
        ((params.divisionNumber ?? 0) > 0 ? (<>
            {t`Division Winner`}<br/>
            {t`Advances to:`}<br/>
            <span><EventLogo logo={params.interdivisionMatchPrefixLogo} /> {params.isFirstChampionship ? (t`${params.interdivisionName} Tournament` ?? t`Championship Playoffs`) : (isQD ? t`Event Playoffs`: t`Event Finals`)}</span>
        </>) : '1st Place');

    const loserTargetString = loserTarget ? <>{t`Advances to:`} <br/> {loserTarget.name} <br/> Will be {loserTargetColor} Alliance</> : 'Eliminated'
    const tieTargetString = <>{t`Will play again in:`} <br /> {seriesDescription?.name.replace(/^Playoff /, '')} {command.params.displayNumber == 1 ? 'Tiebreaker' : `Tiebreaker ${command.params.displayNumber}`}</>
    const sets = (command.params.singleTeam ? ["red"] as const : (config.allianceOrientation === 'flipped' ? ['blue', 'red'] as const : ['red', 'blue'] as const)).map((a, idx) => <ResultsSet
        key={command.params.singleTeam ? 'orange' : a}
        color={command.params.singleTeam ? 'orange' : a}
        alliance={command.params.singleTeam ? command.params.red : command.params[a]}
        scoreDetail={command.params[`${a}Scores`]}
        otherScoreDetail={command.params.singleTeam ? command.params[`${a}Scores`] : command.params[`${a === 'red' ? 'blue' : 'red'}Scores`]}
        tournamentLevel={command.params.tournamentLevel}
        target={!command.params.isEventConclusion && seriesDescription && isDoubleElimsSeriesDescription(seriesDescription) ? (command.params.winner == a.toUpperCase() ? winnerTargetString : (command.params.winner ? loserTargetString : tieTargetString)) : undefined}
        record={seriesDescription && (isDoubleElimsSeriesDescription(seriesDescription) || seriesDescription.matchesToWin <= 1) ? undefined : command.params[a].wins}
        seed={command.params[a].seed}
        score={command.params[`${a}Score`]}
        winner={command.params.winner}
        highScore={command.params[`${a}HighScore`]}
        highScoreFlavor={HIGH_SCORE_TEXT[command.params.highScoreLevel]}
        rightAligned={idx === 1}
        hideRank={command.params.singleTeam || missingLeague}
        showLeagueRankings={showLeagueRankings}
    />);

    const isSyncedLevel = command.params.tournamentLevel == TournamentLevel.PLAYOFF || command.params.tournamentLevel == TournamentLevel.QUALIFICATION;

    const qrBase = state[params.divisionNumber || 0] ? state[params.divisionNumber || 0].url : null;
    const qrElement = qrBase && isSyncedLevel ? <QRCodeSVG size={150} value={qrBase + (command.params.tournamentLevel == TournamentLevel.PLAYOFF ? `/playoff/${command.params.series}/${command.params.displayNumber}` : `/qualifications/${command.params.displayNumber}`)} /> : null;
    const abbreviatedMatchName = command.params.matchName.replace(/of [0-9]+/, '')


    return <PrimaryMatch pageTitle={<><EventLogo logo={params.matchPrefixLogo} /> {command.params.matchName}</>} pageName={t`Match Results`} className={"resultsPage"} smallTop config={config}>
        <MatchVideo video={colorToVideo(command.params.winner)} key={command.ts} mute={config.mute || config.muteRandomizationResults} disabled={videoDisabled}/>
        { running ? t`Results in ${remaining}...` : <div className={`resultsWrapper`}>
            {sets}
            <div className={"resultsBreakdownKey"}>
                <span>{t`LEAVE`}</span>
                <span>{t`ARTIFACT`}</span>
                <span>{t`PATTERN`}</span>
                <span>{t`BASE`}</span>
                <span>{t`FOUL`}</span>
                {command.params.tournamentLevel == TournamentLevel.PLAYOFF && seriesDescription && seriesDescription.matchesToWin > 1 ? <span className={"win-banner"}>{t`WINS`}</span> : null}
            </div>
            {qrElement ? <div className={"resultsQr"}>
                {qrElement}
                <span>{t`Scan for detailed ${abbreviatedMatchName} results`}</span>
            </div> : null}

            {shouldHaveConfetti ? <ReactConfetti width={1920} height={1080} style={{zIndex: 50}} recycle={confettiRunning} numberOfPieces={1000} tweenDuration={10000} /> : null}
        </div>}
    </PrimaryMatch>;
}
